/**
 * BillDateSection — "Bill on" on the Payment terms page (hauler billing gap 6, 0250; PR A review
 * L3: a billing choice, so a billing manager sets it here, as they set payment terms).
 *
 * GET / PATCH /v1/tenants/bill-date-settings: the day each ticket is billed on, the day it
 * finished (the default) or the day it started. The same setting as web-dispatch Settings →
 * Accounting export; the API allows tenant admins and billing managers to change it.
 * The values are local, not imported from @lodex-pro/shared (its barrel pulls drizzle).
 */

import React from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useApiMutation, useApiOpts, useApiQuery } from '@lodex-pro/web-shared';

export type BillDateBasis = 'FINISHED' | 'STARTED';

export const BILL_DATE_QUERY_KEY = ['tenants', 'bill-date-settings'] as const;

export const BILL_DATE_OPTIONS: ReadonlyArray<{
  readonly value: BillDateBasis;
  readonly label: string;
  readonly help: string;
}> = [
  {
    value: 'FINISHED',
    label: 'The day the job finished',
    help: 'Each ticket is dated by the day the driver completed it.',
  },
  {
    value: 'STARTED',
    label: 'The day the job started',
    help: "Each ticket is dated by the day the work began: the driver's arrival, or the start of work, as the customer's billing clock is set.",
  },
];

const helpStyle: React.CSSProperties = {
  fontSize: '13px',
  color: 'var(--color-text-secondary)',
};

export function BillDateSection(): React.JSX.Element {
  const opts = useApiOpts();
  const qc = useQueryClient();
  const [note, setNote] = React.useState<string | null>(null);
  const { data, isLoading, isError } = useApiQuery<{ readonly billDateBasis: BillDateBasis }>(
    opts,
    '/v1/tenants/bill-date-settings',
    { queryKey: BILL_DATE_QUERY_KEY },
  );
  const save = useApiMutation<{ billDateBasis: BillDateBasis }, { billDateBasis: string }>(
    opts,
    '/v1/tenants/bill-date-settings',
    'PATCH',
    {
      onSuccess: () => {
        void qc.invalidateQueries({ queryKey: BILL_DATE_QUERY_KEY });
        setNote('Saved. Tickets approved from now on are dated this way.');
      },
      onError: () => setNote('Could not save the setting. Try again.'),
    },
  );

  return (
    <section style={{ marginTop: '32px' }} data-testid="bill-date-section">
      <h2 style={{ fontWeight: 600, marginBottom: '6px' }}>Bill on</h2>
      <p style={{ ...helpStyle, marginBottom: '10px' }}>
        The date on each invoice line, the PDF, the ticket pages and the QuickBooks export, and the
        day whose disposal fee applies. Changing it only affects tickets approved after the change.
      </p>
      {isError ? (
        <p role="alert" style={{ color: 'var(--color-danger)' }}>
          Failed to load this setting.
        </p>
      ) : isLoading || data === undefined ? (
        <p style={helpStyle}>Loading…</p>
      ) : (
        <div role="radiogroup" aria-label="Bill on" style={{ display: 'grid', gap: '10px' }}>
          {BILL_DATE_OPTIONS.map((o) => (
            <label key={o.value} style={{ display: 'flex', gap: '10px', alignItems: 'flex-start' }}>
              <input
                type="radio"
                name="bill-date-basis"
                value={o.value}
                checked={data.billDateBasis === o.value}
                disabled={save.isPending}
                onChange={(): void => {
                  setNote(null);
                  save.mutate({ billDateBasis: o.value });
                }}
              />
              <span>
                <span style={{ fontWeight: 600 }}>{o.label}</span>
                <span style={{ ...helpStyle, display: 'block' }}>{o.help}</span>
              </span>
            </label>
          ))}
        </div>
      )}
      {note !== null && (
        <p role="status" style={{ ...helpStyle, marginTop: '8px' }}>
          {note}
        </p>
      )}
    </section>
  );
}
